import { DataProxy, FetchResult, useMutation } from '@apollo/client';
import {
  DeleteResultData,
  DeleteResultVars,
  PathwayContentData,
  PathwayContentVars,
  UpdateResultTitleData,
  UpdateResultTitleVars
} from '../gql-types';
import React, { Fragment, MouseEvent, useState } from 'react';

import AlertDialog from '../../shared/components/AlertDialog';
import AuthService from '../../shared/services/AuthService';
import Box from '@mui/material/Box';
import ConfirmationDialog from '../../shared/components/ConfirmationDialog';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import GraphQL from '../gql';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import LessonResultVm from '../models/LessonResultVm';
import LessonService from '../services/LessonService';
import Overlay from '../../shared/components/Overlay';
import Paper from '@mui/material/Paper';
import PathwayLessonVm from '../models/LessonVm';
import PathwayVm from '../models/PathwayVm';
import Typography from '@mui/material/Typography';
import WizardSessionDialog from './WizardSessionDialog';
import { cloneDeep } from 'lodash';

interface WizardSessionCardProps {
  pathkey: string;
  lessonResult: LessonResultVm;
  onLaunchLesson: (
    lessonWindow: Window,
    title: string,
    resultkey: string
  ) => void;
}

const WizardSessionCard = (props: WizardSessionCardProps) => {
  const { title, status } = props.lessonResult;

  const [deleteSession, setDeleteSession] = useState(false);
  const [deletingSession, setDeletingSession] = useState(false);
  const [showDeleteError, setShowDeleteError] = useState(false);
  const [showWizardSessionDialog, setShowWizardSessionDialog] = useState(false);

  const actionText = LessonService.toActionText(status);

  const onDelete = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setDeleteSession(true);
  };

  const onDoDeleteResult = () => {
    setDeletingSession(true);
    deleteResult();
  };
  const onDoDeleteComplete = () => setDeletingSession(false);

  const [deleteResult, { loading, data }] = useMutation<
    DeleteResultData,
    DeleteResultVars
  >(GraphQL.Mutation.DELETE_RESULT, {
    variables: {
      resultkey: props.lessonResult.resultkey
    },
    awaitRefetchQueries: true,
    onCompleted: () => onDoDeleteComplete(),
    onError: () => {
      onDoDeleteComplete();
      setShowDeleteError(true);
    },
    update: (
      proxy: DataProxy,
      mutationResult: FetchResult<{ deleteResult: string }>
    ) => {
      function deleteRecentLesson(pathway: PathwayVm, resultkey: string) {
        const index = pathway.recentLessons.findIndex((lesson) => {
          const resultIndex = lesson.results.findIndex(
            (result) => result.resultkey === resultkey
          );
          return resultIndex !== -1;
        });
        if (index !== -1) {
          pathway.recentLessons.splice(index, 1);
        }
      }
      function deleteResult(lessons: PathwayLessonVm[], resultkey: string) {
        for (let lesson of lessons) {
          const resultIndex = lesson.results.findIndex(
            (result) => result.resultkey === resultkey
          );
          console.debug(`resultIndex: ${resultIndex}`);
          if (resultIndex === -1) {
            continue;
          }
          lesson.results.splice(resultIndex, 1);
          break;
        }
      }
      const { pathwayContent } = proxy.readQuery<
        PathwayContentData,
        PathwayContentVars
      >({
        query: GraphQL.Query.PATHWAY_CONTENT,
        variables: {
          userkey: AuthService.Userkey,
          pathkey: props.pathkey,
          type: 'pathway'
        }
      });
      const pathwayClone = cloneDeep(pathwayContent);
      deleteResult(pathwayClone.lessons, mutationResult.data.deleteResult);
      deleteRecentLesson(pathwayClone, mutationResult.data.deleteResult);
      console.debug(JSON.stringify(pathwayClone));
      proxy.writeQuery<PathwayContentData, PathwayContentVars>({
        query: GraphQL.Query.PATHWAY_CONTENT,
        variables: {
          userkey: AuthService.Userkey,
          pathkey: props.pathkey,
          type: 'pathway'
        },
        data: { pathwayContent: pathwayClone }
      });
    }
  });

  const editResultTitle = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowWizardSessionDialog(true);
  };

  const [
    updateResultTitle,
    {
      called: updateTitleCalled,
      loading: updateTitleLoading,
      error: updateTitleError,
      data: updateTitleData
    }
  ] = useMutation<UpdateResultTitleData, UpdateResultTitleVars>(
    GraphQL.Mutation.UPDATE_RESULT_TITLE
  );

  const editWizardSession = (agreed: boolean, title: string) => {
    if (agreed) {
      updateResultTitle({
        variables: {
          resultkey: props.lessonResult.resultkey,
          title
        },
        update: (
          proxy: DataProxy,
          mutationResult: FetchResult<{ updateResultTitle: LessonResultVm }>
        ) => {
          const { pathwayContent } = proxy.readQuery<
            PathwayContentData,
            PathwayContentVars
          >({
            query: GraphQL.Query.PATHWAY_CONTENT,
            variables: {
              userkey: AuthService.Userkey,
              pathkey: props.pathkey,
              type: 'pathway'
            }
          });
          const pathwayClone = cloneDeep(pathwayContent);
          for (let lesson of pathwayClone.lessons) {
            for (let result of lesson.results) {
              if (result.resultkey === props.lessonResult.resultkey) {
                result.title = title;
                break;
              }
            }
          }
          proxy.writeQuery<PathwayContentData, PathwayContentVars>({
            query: GraphQL.Query.PATHWAY_CONTENT,
            variables: {
              userkey: AuthService.Userkey,
              pathkey: props.pathkey,
              type: 'pathway'
            },
            data: { pathwayContent: pathwayClone }
          });
        }
      });
    }
    setShowWizardSessionDialog(false);
  };

  const handleCardClick = () => {
    const lessonWindow = window.open();
    props.onLaunchLesson(
      lessonWindow,
      props.lessonResult.title,
      props.lessonResult.resultkey
    );
  };

  return (
    <Fragment>
      <Box m={`8px 0`}>
        <Paper style={{ padding: '8px' }} onClick={handleCardClick}>
          <Grid container alignItems="center" wrap="nowrap">
            <Grid>
              <Box marginRight={1}>
                <IconButton size="small" onClick={editResultTitle}>
                  <EditIcon />
                </IconButton>
              </Box>
            </Grid>
            <Grid
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              <Typography variant="body1" noWrap={true}>
                {title}
              </Typography>
            </Grid>
            <Grid style={{ width: '80px', textAlign: 'center' }}>
              <Fragment>
                <Typography
                  variant="button"
                  className={`lesson-status ${status}`}
                  style={{ color: LessonService.getStatusColor(status) }}
                >
                  {LessonService.toStatusText(status)}
                </Typography>
              </Fragment>
            </Grid>
            <Grid>
              <Box marginLeft={1}>
                <IconButton size="small" onClick={onDelete}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      {deleteSession && (
        <ConfirmationDialog
          open={true}
          title={`Are you sure you want to delete session '${title}'`}
          message="Once a session is deleted, there's no way to recover it."
          onClose={async (agreed: boolean) => {
            setDeleteSession(false);
            if (!agreed) {
              return;
            }
            onDoDeleteResult();
          }}
        />
      )}
      {deletingSession && (
        <Overlay spinner={true} message="Deleting session..." />
      )}
      {showDeleteError && (
        <AlertDialog
          title="Failed to delete wizard session"
          message="Please try again."
          onClose={() => setShowDeleteError(false)}
        />
      )}
      {showWizardSessionDialog && (
        <WizardSessionDialog
          title={props.lessonResult.title}
          headerTitle="Edit title"
          onClose={editWizardSession}
        />
      )}
    </Fragment>
  );
};

export default WizardSessionCard;
